import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  IconButton,
  CircularProgress,
  Alert,
  TextField,
  InputAdornment,
  Chip,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  ToggleButton,
  ToggleButtonGroup,
  Menu,
  ListItemIcon,
  ListItemText,
  Snackbar,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Search as SearchIcon,
  Person as PersonIcon,
  AdminPanelSettings as AdminIcon,
  School as CoachIcon,
  IceSkating as SkaterIcon,
  FamilyRestroom as ParentIcon,
  Close as CloseIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Business as ClubIcon,
  CalendarMonth as CalendarIcon,
  AccessTime as TimeIcon,
  Badge as BadgeIcon,
  RemoveRedEye as ImpersonateIcon,
  ContentCopy as CopyIcon,
  Download as DownloadIcon,
  ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';
import { useFirebaseAuth } from '../context/FirebaseAuthContext';
import { useImpersonation } from '../context/ImpersonationContext';
import { REGISTRATION_BASE_URL } from '../config/environment';

const filterOptions = [
  { value: 'all', label: 'All Users' },
  { value: 'interclub_admin', label: 'Interclub Admins' },
  { value: 'coach', label: 'Coaches' },
  { value: 'parent', label: 'Parents' },
];

export default function InterclubUserManagement({ interclubId, interclub }) {
  const { getIdToken, user: currentUser } = useFirebaseAuth();
  const { startImpersonation } = useImpersonation();
  const [searchParams, setSearchParams] = useSearchParams();
  const scopeMyOnly = searchParams.get('scope') === 'my';

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filter, setFilter] = useState('all');
  const [selectedUser, setSelectedUser] = useState(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [sortField, setSortField] = useState('name');
  const [sortDirection, setSortDirection] = useState('asc');

  // Impersonation state
  const [impersonateDialogOpen, setImpersonateDialogOpen] = useState(false);
  const [userToImpersonate, setUserToImpersonate] = useState(null);
  const [impersonating, setImpersonating] = useState(false);
  const [impersonateError, setImpersonateError] = useState(null);

  // Export menu state
  const [exportMenuAnchor, setExportMenuAnchor] = useState(null);
  const [exportSnackbar, setExportSnackbar] = useState({ open: false, message: '' });

  const loadUsers = useCallback(async () => {
    if (!interclubId) return;
    
    try {
      setLoading(true);
      setError(null);
      
      const token = await getIdToken();
      if (!token) {
        throw new Error('Unable to get authentication token');
      }
      
      const seriesParam = scopeMyOnly ? '' : '?series=true';
      const response = await fetch(`${REGISTRATION_BASE_URL}/interclub/${interclubId}/users${seriesParam}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch users');
      }
      
      const data = await response.json();
      setUsers(data.users || []);
    } catch (err) {
      console.error('Error loading users:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [interclubId, scopeMyOnly, getIdToken]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const formatDate = (dateValue) => {
    if (!dateValue) return '—';
    let date;
    if (dateValue._seconds) {
      date = new Date(dateValue._seconds * 1000);
    } else if (dateValue.seconds) {
      date = new Date(dateValue.seconds * 1000);
    } else {
      date = new Date(dateValue);
    }
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const getUserRoles = (user) => {
    const roles = [];
    
    // Check for interclub admin
    if (user.roles?.includes('interclub_admin')) {
      const status = user.approvalStatus?.interclubAdmin;
      roles.push({
        label: 'Interclub Admin',
        color: status === 'approved' ? 'primary' : status === 'pending' ? 'warning' : 'default',
        status,
        icon: <AdminIcon sx={{ fontSize: 14 }} />,
      });
    }
    
    // Check for coach (from roles array or isCoach flag)
    if (user.roles?.includes('coach') || user.isCoach) {
      const status = user.approvalStatus?.coach;
      roles.push({
        label: 'Coach',
        color: status === 'approved' ? 'success' : status === 'pending' ? 'warning' : user.isCoach ? 'success' : 'default',
        status: user.isCoach && !user.roles?.includes('coach') ? 'self-declared' : status,
        icon: <CoachIcon sx={{ fontSize: 14 }} />,
      });
    }
    
    // Check for club admin
    if (user.roles?.includes('club_admin')) {
      const status = user.approvalStatus?.clubAdmin;
      roles.push({
        label: 'Club Admin',
        color: status === 'approved' ? 'info' : status === 'pending' ? 'warning' : 'default',
        status,
        icon: <AdminIcon sx={{ fontSize: 14 }} />,
      });
    }
    
    // Check for adult skater
    if (user.isSkater || user.roles?.includes('adult_skater')) {
      roles.push({
        label: 'Adult Skater',
        color: 'info',
        icon: <SkaterIcon sx={{ fontSize: 14 }} />,
      });
    }
    
    // Check for parent - only if:
    // 1. They explicitly marked themselves as a parent (isParent flag), OR
    // 2. They have skaters registered to them (skaterCount > 0)
    // Don't use legacy 'role' field as it may be outdated/incorrect
    const hasSkaters = (user.skaterCount && user.skaterCount > 0) || (user.skaterIds?.length > 0);
    const isExplicitParent = user.isParent === true;
    
    // Show Parent/Guardian if they have skaters OR explicitly marked as parent
    // (even coaches can be parents if they have their own kids registered)
    if (hasSkaters || isExplicitParent) {
      roles.push({
        label: 'Parent/Guardian',
        color: 'default',
        icon: <ParentIcon sx={{ fontSize: 14 }} />,
      });
    }
    
    // If no roles detected, show as "Member"
    if (roles.length === 0) {
      roles.push({
        label: 'Member',
        color: 'default',
        icon: <PersonIcon sx={{ fontSize: 14 }} />,
      });
    }
    
    return roles;
  };

  const filteredUsers = users.filter(user => {
    const searchLower = searchTerm.toLowerCase();
    const matchesSearch = (
      user.email?.toLowerCase().includes(searchLower) ||
      user.displayName?.toLowerCase().includes(searchLower) ||
      user.firstName?.toLowerCase().includes(searchLower) ||
      user.lastName?.toLowerCase().includes(searchLower)
    );
    
    if (!matchesSearch) return false;
    
    switch (filter) {
      case 'interclub_admin':
        return user.roles?.includes('interclub_admin');
      case 'coach':
        return user.roles?.includes('coach') || user.isCoach;
      case 'parent':
        return user.isParent || (!user.isCoach && !user.isSkater && !user.roles?.includes('interclub_admin'));
      default:
        return true;
    }
  });

  // Display names that appear more than once (for disambiguation)
  const duplicateDisplayNames = useMemo(() => {
    const counts = {};
    filteredUsers.forEach((u) => {
      const name = (u.displayName || `${u.firstName || ''} ${u.lastName || ''}`.trim() || 'Unknown').toLowerCase();
      counts[name] = (counts[name] || 0) + 1;
    });
    return new Set(Object.keys(counts).filter((name) => counts[name] > 1));
  }, [filteredUsers]);

  const sortedUsers = useMemo(() => {
    const dir = sortDirection === 'asc' ? 1 : -1;
    return [...filteredUsers].sort((a, b) => {
      let aVal, bVal;
      switch (sortField) {
        case 'name':
          aVal = (a.displayName || `${a.firstName || ''} ${a.lastName || ''}`.trim() || '').toLowerCase();
          bVal = (b.displayName || `${b.firstName || ''} ${b.lastName || ''}`.trim() || '').toLowerCase();
          return dir * (aVal < bVal ? -1 : aVal > bVal ? 1 : 0);
        case 'club':
          aVal = (a.clubName || '').toLowerCase();
          bVal = (b.clubName || '').toLowerCase();
          return dir * (aVal < bVal ? -1 : aVal > bVal ? 1 : 0);
        case 'joined':
          aVal = a.createdAt?._seconds ?? a.createdAt?.seconds ?? (a.createdAt ? new Date(a.createdAt).getTime() / 1000 : 0);
          bVal = b.createdAt?._seconds ?? b.createdAt?.seconds ?? (b.createdAt ? new Date(b.createdAt).getTime() / 1000 : 0);
          return dir * (aVal - bVal);
        default:
          return 0;
      }
    });
  }, [filteredUsers, sortField, sortDirection]);

  const handleSort = (field) => {
    setSortField(field);
    setSortDirection((prev) => (prev === 'asc' && sortField === field ? 'desc' : 'asc'));
  };

  // Export functions
  const getEmailList = () => {
    return sortedUsers
      .map(u => u.email)
      .filter(Boolean)
      .filter((email, index, self) => self.indexOf(email) === index); // Remove duplicates
  };

  const handleCopyEmails = () => {
    const emails = getEmailList();
    const emailString = emails.join(', ');
    navigator.clipboard.writeText(emailString).then(() => {
      setExportSnackbar({ open: true, message: `Copied ${emails.length} emails to clipboard` });
    }).catch(() => {
      setExportSnackbar({ open: true, message: 'Failed to copy to clipboard' });
    });
    setExportMenuAnchor(null);
  };

  const handleDownloadEmails = () => {
    const emails = getEmailList();
    const csvContent = 'Email\n' + emails.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `${interclub?.name || 'interclub'}_emails_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    URL.revokeObjectURL(link.href);
    setExportSnackbar({ open: true, message: `Downloaded ${emails.length} emails` });
    setExportMenuAnchor(null);
  };

  const handleDownloadFullExport = () => {
    const headers = ['Name', 'Email', 'Club', 'Roles', 'Joined'];
    const rows = sortedUsers.map(u => {
      const name = u.displayName || `${u.firstName || ''} ${u.lastName || ''}`.trim() || 'Unknown';
      const roles = getUserRoles(u).map(r => r.label).join('; ');
      const joined = u.createdAt?._seconds 
        ? new Date(u.createdAt._seconds * 1000).toLocaleDateString()
        : u.createdAt ? new Date(u.createdAt).toLocaleDateString() : '';
      return [name, u.email || '', u.clubName || '', roles, joined];
    });
    const csvContent = [headers, ...rows].map(row => row.map(cell => `"${(cell || '').replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `${interclub?.name || 'interclub'}_users_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    URL.revokeObjectURL(link.href);
    setExportSnackbar({ open: true, message: `Downloaded ${sortedUsers.length} users` });
    setExportMenuAnchor(null);
  };

  const handleUserClick = (user) => {
    setSelectedUser(user);
    setDetailDialogOpen(true);
  };

  const handleCloseDetail = () => {
    setDetailDialogOpen(false);
    setSelectedUser(null);
  };

  // Impersonation handlers
  const handleImpersonateClick = (user, e) => {
    if (e) e.stopPropagation();
    setUserToImpersonate(user);
    setImpersonateError(null);
    setImpersonateDialogOpen(true);
  };

  const handleConfirmImpersonate = async () => {
    if (!userToImpersonate) return;

    try {
      setImpersonating(true);
      setImpersonateError(null);
      const token = await getIdToken();

      // Validate impersonation permission - backend will check interclub membership
      const response = await fetch(`${REGISTRATION_BASE_URL}/impersonation/validate`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          targetUserId: userToImpersonate.id,
          interclubId, // Send interclub context for validation
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to validate impersonation');
      }

      const result = await response.json();

      // Start impersonation
      startImpersonation({
        id: result.targetUser.id,
        email: result.targetUser.email,
        displayName: result.targetUser.displayName ||
          `${result.targetUser.firstName || ''} ${result.targetUser.lastName || ''}`.trim(),
      });

      setImpersonateDialogOpen(false);
      setUserToImpersonate(null);

      // Redirect to home to see the impersonated user's view
      window.location.href = '/';
    } catch (err) {
      console.error('Impersonation failed:', err);
      setImpersonateError(err.message);
    } finally {
      setImpersonating(false);
    }
  };

  const handleCloseImpersonateDialog = () => {
    setImpersonateDialogOpen(false);
    setUserToImpersonate(null);
    setImpersonateError(null);
  };

  // Check if we can impersonate a user (not ourselves)
  const canImpersonate = (user) => currentUser && user.id !== currentUser.uid;

  // Calculate stats
  const stats = {
    total: users.length,
    admins: users.filter(u => u.roles?.includes('interclub_admin') && u.approvalStatus?.interclubAdmin === 'approved').length,
    coaches: users.filter(u => (u.roles?.includes('coach') && u.approvalStatus?.coach === 'approved') || u.isCoach).length,
    parents: users.filter(u => u.isParent || (!u.isCoach && !u.isSkater)).length,
  };

  return (
    <Paper elevation={0} sx={{ p: 3, borderRadius: 4, border: '1px solid', borderColor: 'divider' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box sx={{ 
            p: 1.5, 
            borderRadius: 2, 
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            display: 'flex',
            boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)'
          }}>
            <PersonIcon sx={{ color: 'white', fontSize: 28 }} />
          </Box>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              Users
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {scopeMyOnly ? 'Users in your interclub only' : 'Users across all interclubs in the series'}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ToggleButtonGroup
            value={scopeMyOnly ? 'my' : 'all'}
            exclusive
            onChange={(_, value) => {
              if (value == null) return;
              const next = new URLSearchParams(searchParams);
              if (value === 'my') next.set('scope', 'my');
              else next.delete('scope');
              setSearchParams(next);
            }}
            size="small"
            sx={{ mr: 1 }}
          >
            <ToggleButton value="all">All interclubs</ToggleButton>
            <ToggleButton value="my">{interclub?.name ? `${interclub.name} only` : 'My interclub only'}</ToggleButton>
          </ToggleButtonGroup>
          <IconButton
            onClick={loadUsers}
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2
            }}
          >
            <RefreshIcon />
          </IconButton>
        </Box>
      </Box>

      {/* Stats */}
      <Box sx={{ display: 'flex', gap: 1.5, mb: 3, flexWrap: 'wrap', alignItems: 'center' }}>
        <Chip 
          label={`Total: ${stats.total}`} 
          variant="outlined"
          sx={{ fontWeight: 600 }}
        />
        <Chip 
          icon={<AdminIcon sx={{ fontSize: 16 }} />}
          label={`Admins: ${stats.admins}`} 
          sx={{ bgcolor: 'primary.50', color: 'primary.dark', fontWeight: 600, border: '1px solid', borderColor: 'primary.200' }}
        />
        <Chip 
          icon={<CoachIcon sx={{ fontSize: 16 }} />}
          label={`Coaches: ${stats.coaches}`} 
          sx={{ bgcolor: 'success.50', color: 'success.dark', fontWeight: 600, border: '1px solid', borderColor: 'success.200' }}
        />
        <Chip 
          icon={<ParentIcon sx={{ fontSize: 16 }} />}
          label={`Parents: ${stats.parents}`} 
          variant="outlined"
          sx={{ fontWeight: 600 }}
        />
        <Box sx={{ flex: 1 }} />
        <Button
          variant="outlined"
          size="small"
          startIcon={<EmailIcon />}
          endIcon={<ExpandMoreIcon />}
          onClick={(e) => setExportMenuAnchor(e.currentTarget)}
          disabled={sortedUsers.length === 0}
          sx={{ borderRadius: 2 }}
        >
          Export Emails ({getEmailList().length})
        </Button>
        <Menu
          anchorEl={exportMenuAnchor}
          open={Boolean(exportMenuAnchor)}
          onClose={() => setExportMenuAnchor(null)}
          PaperProps={{ sx: { borderRadius: 2, minWidth: 220 } }}
        >
          <MenuItem onClick={handleCopyEmails}>
            <ListItemIcon><CopyIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Copy emails to clipboard</ListItemText>
          </MenuItem>
          <MenuItem onClick={handleDownloadEmails}>
            <ListItemIcon><DownloadIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Download emails (CSV)</ListItemText>
          </MenuItem>
          <Divider />
          <MenuItem onClick={handleDownloadFullExport}>
            <ListItemIcon><DownloadIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Download full user list (CSV)</ListItemText>
          </MenuItem>
        </Menu>
      </Box>

      {/* Filters */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={4}>
          <FormControl fullWidth size="small">
            <InputLabel>Filter</InputLabel>
            <Select
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              label="Filter"
              sx={{ borderRadius: 2 }}
            >
              {filterOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6} md={8}>
          <TextField
            fullWidth
            variant="outlined"
            placeholder="Search users..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            size="small"
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
            }}
          />
        </Grid>
      </Grid>

      {error && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
          {error}
          <Button onClick={loadUsers} sx={{ ml: 2 }} size="small">
            Retry
          </Button>
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : filteredUsers.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 6, px: 3, bgcolor: 'grey.50', borderRadius: 3 }}>
          <PersonIcon sx={{ fontSize: 48, color: 'grey.400', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            {searchTerm || filter !== 'all' ? 'No users found' : 'No users yet'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {searchTerm ? 'Try adjusting your search terms.' : 'Users who register with your interclub will appear here.'}
          </Typography>
        </Box>
      ) : (
        <TableContainer sx={{ 
          border: '1px solid', 
          borderColor: 'divider', 
          borderRadius: 2,
          '& .MuiTableCell-head': {
            bgcolor: 'grey.50',
            fontWeight: 700
          }
        }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sortDirection={sortField === 'name' ? sortDirection : false}>
                  <TableSortLabel
                    active={sortField === 'name'}
                    direction={sortField === 'name' ? sortDirection : 'asc'}
                    onClick={() => handleSort('name')}
                  >
                    User
                  </TableSortLabel>
                </TableCell>
                <TableCell>Roles & Status</TableCell>
                <TableCell sortDirection={sortField === 'club' ? sortDirection : false}>
                  <TableSortLabel
                    active={sortField === 'club'}
                    direction={sortField === 'club' ? sortDirection : 'asc'}
                    onClick={() => handleSort('club')}
                  >
                    Club
                  </TableSortLabel>
                </TableCell>
                <TableCell sortDirection={sortField === 'joined' ? sortDirection : false}>
                  <TableSortLabel
                    active={sortField === 'joined'}
                    direction={sortField === 'joined' ? sortDirection : 'asc'}
                    onClick={() => handleSort('joined')}
                  >
                    Joined
                  </TableSortLabel>
                </TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedUsers.map((user) => {
                const roles = getUserRoles(user);
                return (
                  <TableRow 
                    key={user.id} 
                    hover 
                    onClick={() => handleUserClick(user)}
                    sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'primary.50' } }}
                  >
                    <TableCell>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: 'primary.main' }}>
                          {(() => {
                            const name = user.displayName || `${user.firstName || ''} ${user.lastName || ''}`.trim() || 'Unknown';
                            const nameLower = name.toLowerCase();
                            const isDuplicate = duplicateDisplayNames.has(nameLower);
                            return isDuplicate ? `${name} (${user.email})` : name;
                          })()}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {user.email}
                        </Typography>
                        {user.phone && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            {user.phone}
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        {roles.map((role, index) => (
                          <Tooltip 
                            key={index} 
                            title={role.status ? `Status: ${role.status}` : ''}
                            arrow
                          >
                            <Chip
                              icon={role.icon}
                              label={role.label}
                              size="small"
                              color={role.color}
                              variant={role.status === 'pending' ? 'outlined' : 'filled'}
                              sx={{ 
                                fontWeight: 600,
                                '& .MuiChip-icon': { ml: 0.5 }
                              }}
                            />
                          </Tooltip>
                        ))}
                        {roles.length === 0 && (
                          <Typography variant="caption" color="text.secondary">
                            No roles assigned
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {user.clubName || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {formatDate(user.createdAt)}
                      </Typography>
                      {user.lastSignIn && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                          Last active: {formatDate(user.lastSignIn)}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="center">
                      {canImpersonate(user) && (
                        <Tooltip title="View as this user (troubleshooting)">
                          <IconButton
                            size="small"
                            color="warning"
                            onClick={(e) => handleImpersonateClick(user, e)}
                          >
                            <ImpersonateIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Box sx={{ mt: 2 }}>
        <Typography variant="body2" color="text.secondary">
          Showing {sortedUsers.length} of {stats.total} user{stats.total !== 1 ? 's' : ''}
        </Typography>
      </Box>

      {/* User Detail Dialog */}
      <Dialog 
        open={detailDialogOpen} 
        onClose={handleCloseDetail}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        {selectedUser && (
          <>
            <DialogTitle sx={{ pb: 1 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box sx={{ 
                    p: 1.5, 
                    borderRadius: 2, 
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    display: 'flex',
                  }}>
                    <PersonIcon sx={{ color: 'white', fontSize: 28 }} />
                  </Box>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                      {selectedUser.displayName || `${selectedUser.firstName || ''} ${selectedUser.lastName || ''}`.trim() || 'Unknown User'}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5, flexWrap: 'wrap' }}>
                      {getUserRoles(selectedUser).map((role, index) => (
                        <Chip
                          key={index}
                          icon={role.icon}
                          label={role.label}
                          size="small"
                          color={role.color}
                          variant={role.status === 'pending' ? 'outlined' : 'filled'}
                          sx={{ fontWeight: 600, '& .MuiChip-icon': { ml: 0.5 } }}
                        />
                      ))}
                    </Box>
                  </Box>
                </Box>
                <IconButton onClick={handleCloseDetail} size="small">
                  <CloseIcon />
                </IconButton>
              </Box>
            </DialogTitle>
            <DialogContent dividers>
              <Grid container spacing={2}>
                {/* Contact Information */}
                <Grid item xs={12}>
                  <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 600 }}>
                    Contact Information
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <EmailIcon color="action" fontSize="small" />
                    <Box>
                      <Typography variant="caption" color="text.secondary">Email</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {selectedUser.email || 'Not set'}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PhoneIcon color="action" fontSize="small" />
                    <Box>
                      <Typography variant="caption" color="text.secondary">Phone</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {selectedUser.phone || 'Not set'}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>

                <Grid item xs={12}><Divider sx={{ my: 1 }} /></Grid>

                {/* Club & Skaters */}
                <Grid item xs={12}>
                  <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 600 }}>
                    Club & Skaters
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <ClubIcon color="action" fontSize="small" />
                    <Box>
                      <Typography variant="caption" color="text.secondary">Club</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {selectedUser.clubName || 'No club assigned'}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <SkaterIcon color="action" fontSize="small" />
                    <Box>
                      <Typography variant="caption" color="text.secondary">Skaters</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {selectedUser.skaterCount || 0} skater{selectedUser.skaterCount !== 1 ? 's' : ''}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>

                <Grid item xs={12}><Divider sx={{ my: 1 }} /></Grid>

                {/* Account Details */}
                <Grid item xs={12}>
                  <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 600 }}>
                    Account Details
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <BadgeIcon color="action" fontSize="small" />
                    <Box>
                      <Typography variant="caption" color="text.secondary">User ID</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600, fontFamily: 'monospace', fontSize: '0.75rem' }}>
                        {selectedUser.id}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CalendarIcon color="action" fontSize="small" />
                    <Box>
                      <Typography variant="caption" color="text.secondary">Joined</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {formatDate(selectedUser.createdAt)}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <TimeIcon color="action" fontSize="small" />
                    <Box>
                      <Typography variant="caption" color="text.secondary">Last Active</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {formatDate(selectedUser.lastSignIn) || 'Never'}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>

                {/* Role Approval Details */}
                {(selectedUser.approvalStatus?.interclubAdmin || 
                  selectedUser.approvalStatus?.coach || 
                  selectedUser.approvalStatus?.clubAdmin) && (
                  <>
                    <Grid item xs={12}><Divider sx={{ my: 1 }} /></Grid>
                    <Grid item xs={12}>
                      <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 600 }}>
                        Approval Status
                      </Typography>
                    </Grid>
                    {selectedUser.approvalStatus?.interclubAdmin && (
                      <Grid item xs={12} sm={6}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <AdminIcon color="action" fontSize="small" />
                          <Box>
                            <Typography variant="caption" color="text.secondary">Interclub Admin</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 600, textTransform: 'capitalize' }}>
                              {selectedUser.approvalStatus.interclubAdmin}
                            </Typography>
                          </Box>
                        </Box>
                      </Grid>
                    )}
                    {selectedUser.approvalStatus?.coach && (
                      <Grid item xs={12} sm={6}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <CoachIcon color="action" fontSize="small" />
                          <Box>
                            <Typography variant="caption" color="text.secondary">Coach</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 600, textTransform: 'capitalize' }}>
                              {selectedUser.approvalStatus.coach}
                            </Typography>
                          </Box>
                        </Box>
                      </Grid>
                    )}
                    {selectedUser.approvalStatus?.clubAdmin && (
                      <Grid item xs={12} sm={6}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <AdminIcon color="action" fontSize="small" />
                          <Box>
                            <Typography variant="caption" color="text.secondary">Club Admin</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 600, textTransform: 'capitalize' }}>
                              {selectedUser.approvalStatus.clubAdmin}
                            </Typography>
                          </Box>
                        </Box>
                      </Grid>
                    )}
                  </>
                )}

                {/* Additional Info */}
                {(selectedUser.isCoach || selectedUser.isParent || selectedUser.isSkater) && (
                  <>
                    <Grid item xs={12}><Divider sx={{ my: 1 }} /></Grid>
                    <Grid item xs={12}>
                      <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 600 }}>
                        Account Flags
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        {selectedUser.isCoach && (
                          <Chip label="Self-declared Coach" size="small" color="success" variant="outlined" />
                        )}
                        {selectedUser.isParent && (
                          <Chip label="Is Parent/Guardian" size="small" variant="outlined" />
                        )}
                        {selectedUser.isSkater && (
                          <Chip label="Is Adult Skater" size="small" color="info" variant="outlined" />
                        )}
                      </Box>
                    </Grid>
                  </>
                )}
              </Grid>
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2, justifyContent: 'space-between' }}>
              {canImpersonate(selectedUser) && (
                <Button
                  onClick={() => handleImpersonateClick(selectedUser)}
                  variant="outlined"
                  color="warning"
                  startIcon={<ImpersonateIcon />}
                  sx={{ borderRadius: 2 }}
                >
                  View as User
                </Button>
              )}
              <Button onClick={handleCloseDetail} variant="outlined" sx={{ borderRadius: 2 }}>
                Close
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Impersonate Confirmation Dialog */}
      <Dialog
        open={impersonateDialogOpen}
        onClose={handleCloseImpersonateDialog}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ImpersonateIcon color="warning" />
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              View as User
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          {impersonateError && (
            <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
              {impersonateError}
            </Alert>
          )}
          <Typography variant="body2" sx={{ mb: 2 }}>
            You are about to view the application as:
          </Typography>
          {userToImpersonate && (
            <Paper
              variant="outlined"
              sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 2 }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                {userToImpersonate.displayName ||
                  `${userToImpersonate.firstName || ''} ${userToImpersonate.lastName || ''}`.trim() ||
                  'Unknown User'}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {userToImpersonate.email}
              </Typography>
            </Paper>
          )}
          <Alert severity="info" sx={{ mt: 2, borderRadius: 2 }}>
            This allows you to troubleshoot issues by seeing exactly what the user sees.
            Click "Exit" in the red banner to return to your admin view.
          </Alert>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={handleCloseImpersonateDialog}
            disabled={impersonating}
            sx={{ borderRadius: 2 }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmImpersonate}
            variant="contained"
            color="warning"
            disabled={impersonating}
            startIcon={impersonating ? <CircularProgress size={16} color="inherit" /> : <ImpersonateIcon />}
            sx={{ borderRadius: 2 }}
          >
            {impersonating ? 'Starting...' : 'View as User'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Export Snackbar */}
      <Snackbar
        open={exportSnackbar.open}
        autoHideDuration={3000}
        onClose={() => setExportSnackbar({ open: false, message: '' })}
        message={exportSnackbar.message}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Paper>
  );
}
